import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ModalService } from '@revspringinc/rs-shared';
import { TranslateService } from '@ngx-translate/core';
import { EstimateComponent, EstimateConfigModel } from '@estimate';
import { Alert, AmaNoticeService } from '@revspringinc/shared';
import { ShoppingSessionService } from '../main/session.service';
import { OrganizationService } from '../main/organization.service';

import * as Sentry from '@sentry/angular';
import { LocalStorageService } from '../services/local-storage.service';
import {
    EstimateService,
    LocationService,
    OrganizationLocation,
    ProcedureGroupModel,
    ProcedureResponseModel,
    ProcedureService,
    SessionService,
    ShoppingSessionResponseModel,
    SiteConfigService,
} from '@shopping';
import * as _ from 'lodash-es';
import { catchError, filter, takeUntil } from 'rxjs/operators';
import { ServerSentEventsService } from '../services/sse.service';
import { ApiService } from '../services/api.service';
import { BenefitStatus } from '@encounter';
import { EMPTY, lastValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { BaseComponent } from '@pa-ui';

@Component({
    selector: 'rs-estimate-service',
    templateUrl: './estimate-service.component.html',
    styleUrls: ['./estimate-service.component.scss'],
    standalone: false,
})
export class EstimateServiceComponent extends BaseComponent implements OnInit {
    @ViewChild(EstimateComponent, { read: ElementRef }) public estimateView: ElementRef;

    public locations: OrganizationLocation[];
    public features: any;
    public loading = false;
    public estimate: EstimateConfigModel;
    public estimateLanguage: Record<string, Record<string, unknown>> | undefined;
    public hasZeroProcedure = false;
    public alert: Alert = null;
    public errorConfig: any;
    public estimateDisclaimerAcknowledged: Date;
    public estimateDisclaimerConfig: any;
    public unsupportedProceduresConfig: any;
    public orgLayoutConfig: any;
    public notPerformedProcedureList: { procedureCode: string; name: string }[] = [];
    private notPerformedLoaded = false;
    public printConfig = {
        organizationName: '',
        locationName: '',
        dateTimeStamp: '',
        insurance: '',
    };

    public estimateItems = [
        {
            id: 0,
            label: 'Services',
            placeholder: 'Select Services',
            value: null,
            selectedCount: 0,
        },
        {
            id: 2,
            label: 'Insurance',
            placeholder: 'Select Insurance',
            value: null,
        },
    ];

    // maybe add this to the site config
    public notPerformedProcedures = {
        label: 'Procedures Not Performed',
        placeholder: 'View Procedures',
        allPerformedNote: 'All procedures are performed',
        value: null,
    };

    private selectedProcedures: ProcedureGroupModel[] = [];
    private sessionId: string;
    private planName: string;
    private payerName: string;
    private benefitStatus: string;
    private locationId: number;
    private procedureGroupsTranslation: ProcedureResponseModel;

    constructor(
        private readonly modalService: ModalService,
        private readonly amaNoticeService: AmaNoticeService,
        private readonly translate: TranslateService,
        private readonly sessionService: SessionService,
        private readonly shoppingSessionService: ShoppingSessionService,
        private readonly organizationService: OrganizationService,
        private readonly estimateService: EstimateService,
        private readonly localStorageService: LocalStorageService,
        private readonly locationService: LocationService,
        private readonly sseService: ServerSentEventsService,
        private readonly procedureService: ProcedureService,
        private readonly api: ApiService,
        private readonly siteConfig: SiteConfigService,
        private readonly router: Router,
    ) {
        super();
    }

    public async ngOnInit(): Promise<void> {
        // subscribe to any session changes
        this.shoppingSessionService.session.subscribe(async (session) => {
            this.sessionId = session.sessionId;

            await this.loadFeatures();
            await this.setSiteConfigs(session);
            await this.loadProcedures();

            await this.syncLatestSessionUpdates(session);

            // Load $0 estimate initially
            this.pingForEstimate({ waitForResponse: false });
        });

        // subscribe to backend estimate updates sent via SSE
        this.shoppingSessionService.estimateUpdated.pipe(filter((x) => !!x)).subscribe((eventData) => {
            // get the updated estimate
            if (eventData) {
                this.pingForEstimate({ waitForResponse: false });
            }
        });
    }

    public servicesLocked(): boolean {
        return this.features?.truePriceShowLocations && !this.locationId;
    }

    private async loadFeatures() {
        this.features = await lastValueFrom(this.api.getFeatures(this.sessionId));
    }

    private async loadProcedures() {
        const locationId = this.features?.truePriceShowLocations && this.locationId ? this.locationId : undefined;

        this.procedureGroupsTranslation = await lastValueFrom(
            this.procedureService.procedureApiControllerGetAll(
                this.sessionId,
                undefined, // limit
                undefined, // index
                undefined, // searchTerm
                locationId,
            ),
        );
    }

    private setEstimateAcknowledged(session: ShoppingSessionResponseModel) {
        this.estimateDisclaimerAcknowledged = session.estimateDisclaimerAcknowledged;
    }

    private async setSiteConfigs(session: ShoppingSessionResponseModel) {
        if (this.features.truePriceRequireEstimateDisclaimer && !session?.estimateDisclaimerAcknowledged) {
            this.estimateDisclaimerConfig = await lastValueFrom(
                this.siteConfig.siteConfigApiControllerGetSiteConfig(this.sessionId, 'estimate-disclaimer'),
            );

            Object.keys(this.estimateDisclaimerConfig.language).forEach((lang) =>
                this.translate.setTranslation(lang, this.estimateDisclaimerConfig.language[lang], true),
            );
        }

        this.siteConfig
            .siteConfigApiControllerGetSiteConfig(this.sessionId, 'unsupported-procedures')
            .pipe(
                takeUntil(this.subscriptionNotifier),
                catchError(() => {
                    return EMPTY;
                }),
            )
            .subscribe(async (config) => {
                if (config?.config?.proceduresNotPerformed) {
                    this.unsupportedProceduresConfig = config;
                    await this.loadNotPerformedProcedures();
                }
            });

        this.siteConfig
            .siteConfigApiControllerGetSiteConfig(this.sessionId, 'org-layout')
            .pipe(
                takeUntil(this.subscriptionNotifier),
                catchError(() => {
                    return EMPTY;
                }),
            )
            .subscribe((config) => {
                if (config?.language) {
                    Object.keys(config.language).forEach((lang) =>
                        this.translate.setTranslation(lang, config.language[lang], true),
                    );
                }

                this.orgLayoutConfig = config;
            });
    }

    private getCuratedProcedures(): { procedureCode: string; name: string }[] {
        const procedures = this.unsupportedProceduresConfig?.config?.procedures ?? [];

        return procedures.map((entry: { procedureCode: string; name: string }) => ({
            procedureCode: entry.procedureCode,
            name: entry.name,
        }));
    }

    private async loadNotPerformedProcedures(): Promise<void> {
        this.notPerformedLoaded = false;

        if (!this.unsupportedProceduresConfig) {
            this.notPerformedProcedureList = [];

            return;
        }

        const curated = this.getCuratedProcedures();

        if (!curated.length) {
            this.notPerformedProcedureList = [];

            return;
        }

        const locationsEnabled = !!this.features?.truePriceShowLocations;

        if (locationsEnabled && !this.locationId) {
            this.notPerformedProcedureList = [];

            return;
        }

        const locationId = locationsEnabled ? this.locationId : undefined;

        try {
            const { notPerformed } = await lastValueFrom(
                this.api.getChargeMasterAvailability(
                    this.sessionId,
                    curated.map((p) => p.procedureCode),
                    locationId,
                ),
            );

            const notPerformedSet = new Set(notPerformed);
            this.notPerformedProcedureList = curated.filter((p) => notPerformedSet.has(p.procedureCode));
            this.notPerformedLoaded = true;
        } catch (error) {
            Sentry.captureException(error);
            this.notPerformedProcedureList = [];
        }
    }

    public showNotPerformedProcedures(): boolean {
        return !!this.unsupportedProceduresConfig;
    }

    public allProceduresPerformed(): boolean {
        return this.notPerformedLoaded && !this.notPerformedProcedureList.length;
    }

    public notPerformedProceduresDisabled(): boolean {
        return (!!this.features?.truePriceShowLocations && !this.locationId) || this.allProceduresPerformed();
    }

    public async estimateInfo(id: number) {
        switch (id) {
            case 0: {
                if (this.servicesLocked()) {
                    return;
                }

                const lastSavedSelectedProcedures = this.selectedProcedures;

                if (!this.amaNoticeService.isViewed()) {
                    await lastValueFrom(this.modalService.show('AmaNoticeModal'));
                }

                const modalData = (await lastValueFrom(
                    this.modalService.show('SelectServicesModal', {
                        selectedProcedures: lastSavedSelectedProcedures,
                        locationId: this.locationId,
                        locations: this.locations,
                        hasLocationEnabled: this.features?.truePriceShowLocations,
                    }),
                )) as { procedures: ProcedureGroupModel[]; location?: OrganizationLocation };

                if (modalData) {
                    await this.setSelectedProcedures(modalData.procedures);
                    if (modalData.location) {
                        await this.setSelectedLocation(modalData.location);
                    }
                    await this.syncLatestSessionUpdates();
                    this.loading = true;
                } else {
                    this.selectedProcedures = lastSavedSelectedProcedures;
                }

                break;
            }
            case 1: {
                const modalData = (await lastValueFrom(
                    this.modalService.show('SelectLocationModal', {
                        locations: this.locations,
                        selectedProcedures: this.selectedProcedures,
                        locationId: this.locationId,
                    }),
                )) as { location: OrganizationLocation; procedures: ProcedureGroupModel[] };

                if (modalData) {
                    await this.setSelectedLocation(modalData.location);
                    await this.syncLatestSessionUpdates();
                }

                this.printConfig.locationName = modalData?.location?.name;

                break;
            }
            case 2: {
                const insuranceEstimateItem = this.estimateItems.find((x) => x.id === 2);

                const modalData = await lastValueFrom(
                    this.modalService.show('AddInsurancePlanModal', insuranceEstimateItem.value),
                );
                const cancelled = modalData.cancel ?? false;

                if (cancelled) {
                    break;
                }

                await this.syncLatestSessionUpdates();

                this.printConfig.insurance = this.payerName + '-' + this.planName;

                break;
            }
        }
    }

    public openNotPerformedProcedures() {
        if (this.notPerformedProceduresDisabled()) {
            return;
        }

        this.modalService.show('ProceduresNotPerformedModal', {
            config: {
                language: this.unsupportedProceduresConfig?.language,
                config: {
                    procedures: this.notPerformedProcedureList,
                },
            },
        });
    }

    public async toEstimateActions(action: any) {
        switch (action.id) {
            case 1: {
                this.openContactMe();
                break;
            }
            case 4: {
                this.downloadPdf();
                break;
            }
            case 3: {
                this.modalService.show('CompareCostsModal');
                break;
            }
            case 5: {
                this.modalService.show('TextEstimateSummaryModal', this.generateTextSummary());
                break;
            }
            case 6: {
                await this.router.navigate(['external-redirect', { externalUrl: action.link }], {
                    skipLocationChange: true,
                });
                break;
            }
        }
    }

    public openContactMe(): void {
        const currentProcedures = this.selectedProcedures?.map((x) => this.translate.instant(x.name)) ?? [];
        this.modalService.show('ContactMeModal', { currentProcedures });
    }

    public inlineCss(sourceElement: any, targetElement: any) {
        Array.from(targetElement.children).forEach((child, index) => {
            this.inlineCss(sourceElement.children[index], child);
        });

        const computedStyle = getComputedStyle(sourceElement);

        Array.from(computedStyle).forEach((property) => {
            targetElement.style[property] = computedStyle.getPropertyValue(property);
        });
    }

    public pingForEstimate(options: { waitForResponse: boolean; maxAttempts?: number; delay?: number }) {
        this.loading = true;

        let maxAttempts = options?.maxAttempts;
        let delay = options?.delay;
        let waitForResponse = options?.waitForResponse;

        setTimeout(() => {
            this.errorConfig = (this.estimate as any)?.error || {
                maxAttempts: 10,
                delay: 1,
                message: 'An unknown error occurred.  Please try again.',
                priority: 'danger',
                icon: 'icon-alert',
            };

            if (!maxAttempts) {
                maxAttempts = this.errorConfig.maxAttempts;
            }

            if (!delay) {
                delay = this.errorConfig.delay;
            }

            if (maxAttempts <= 1) {
                this.alert = this.errorConfig;

                console.error('Maximum retries attempted.');
                this.loading = false;

                return;
            }

            // reset the alert
            this.alert = null;

            this.estimateService
                .estimationApiControllerEstimateLatest(this.sessionId)
                .pipe(
                    takeUntil(this.subscriptionNotifier),
                    catchError((response) => {
                        Sentry.captureException(response);

                        this.alert = {
                            icon: 'icon-alert',
                            priority: 'danger',
                            message: `${response.statusText} - Retrying`,
                        };

                        this.pingForEstimate({
                            waitForResponse: false,
                            maxAttempts: --maxAttempts,
                            delay,
                        });

                        return EMPTY;
                    }),
                )
                .subscribe((estimateData: any) => {
                    Object.keys(estimateData.language).forEach((lang) =>
                        this.translate.setTranslation(lang, estimateData.language[lang], true),
                    );

                    if (!waitForResponse) {
                        this.loading = false;
                        this.estimate = estimateData.estimate;
                        this.estimateLanguage = estimateData.language;
                        this.hasZeroProcedure = this.computeHasZeroProcedure();

                        if (estimateData.status === 'unable to estimate') {
                            this.alert = {
                                icon: 'icon-alert',
                                priority: 'danger',
                                message: `${estimateData.message.join(' ')}`,
                            };
                        }

                        return;
                    }
                });
        }, delay);
    }

    public async restartSession(): Promise<void> {
        this.organizationService.organization.subscribe(async (organization) => {
            this.locationId = null;
            await this.sseService.closeServerSentEvent(this.sessionId);
            await this.shoppingSessionService.createSession(organization, true);
            this.localStorageService.setSessionId(this.shoppingSessionService.sessionId);
            this.setLocationDisplayName(null);
            await this.setSessionSelectedProcedures(null);
            this.setInsuranceDisplayName(null);
            await this.setPrintHeader(null);
        });
    }

    public locationsReady() {
        return (
            (this.features?.truePriceShowLocations && this.locations) ||
            (this.features && !this.features?.truePriceShowLocations)
        );
    }

    public async setEstimateDisclaimerAcknowledged() {
        await this.shoppingSessionService.estimateDisclaimerAcknowledged();
        await this.syncLatestSessionUpdates();
    }

    public blockedByDisclaimer() {
        return (
            this.features && this.features.truePriceRequireEstimateDisclaimer && !this.estimateDisclaimerAcknowledged
        );
    }

    private computeHasZeroProcedure(): boolean {
        const serviceGroups = this.estimate?.serviceGroups;

        if (!serviceGroups?.length) {
            return false;
        }

        return serviceGroups.some((serviceGroup) =>
            (serviceGroup.providerGroups ?? []).some((providerGroup) =>
                (providerGroup.services ?? []).some((procedure) => procedure.patientResponsibility === 0),
            ),
        );
    }

    private async setPrintHeader(session: ShoppingSessionResponseModel) {
        const gmtDate = new Date();
        const localDate = gmtDate.toLocaleString('en', {
            timeZoneName: 'short',
            hour: 'numeric',
            second: 'numeric',
            minute: 'numeric',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            weekday: 'short',
        });

        this.printConfig.organizationName = session?.orgDisplayName;
        this.printConfig.locationName = session?.locationName;

        this.printConfig.dateTimeStamp = localDate;
        this.printConfig.insurance = session?.insurance
            ? session?.insurance?.payerName + ' - ' + session?.insurance?.planName
            : null;
    }

    private setLocationDisplayName(locationName: string | null) {
        const locationEstimateItem = this.estimateItems.find((x) => x.id === 1);

        locationEstimateItem.value = locationName ? [locationName] : null;
        locationEstimateItem.selectedCount = 0;
    }

    private setInsuranceDisplayName(session: ShoppingSessionResponseModel) {
        const insurance = session?.insurance;
        const insuranceEstimateItem = this.estimateItems.find((x) => x.id === 2);

        this.payerName = insurance ? insurance?.payerName : null;
        this.planName = insurance ? insurance?.planName : null;
        this.benefitStatus = session?.benefitStatus;

        if (this.payerName !== 'SELFPAY') {
            switch (this.benefitStatus) {
                case BenefitStatus.Active:
                    insuranceEstimateItem.value = insurance
                        ? [insurance?.payerName + ' - Your insurance was verified.']
                        : [];
                    break;
                case BenefitStatus.NotActive:
                    insuranceEstimateItem.value = insurance
                        ? [insurance?.payerName + ' - Your insurance was verified, but your coverage is not active.']
                        : [];
                    break;
                case BenefitStatus.MemberNotFound:
                    insuranceEstimateItem.value = insurance
                        ? [
                              insurance?.payerName +
                                  ' - Your insurance was verified, but the provided member was not found.',
                          ]
                        : [];
                    break;
                case BenefitStatus.InsufficientSearchCriteria:
                    insuranceEstimateItem.value = insurance
                        ? [insurance?.payerName + ' - There was not enough information to verify your insurance.']
                        : [];
                    break;
                case BenefitStatus.InsuranceNotUsed:
                    insuranceEstimateItem.value = insurance
                        ? [insurance?.payerName + ' - You are not using insurance.']
                        : [];
                    break;
                case BenefitStatus.NotRun:
                    insuranceEstimateItem.value = insurance
                        ? [insurance?.payerName + ' - Eligibility was not run for your insurance.']
                        : [];
                    break;
                case BenefitStatus.PayerUnavailable:
                    insuranceEstimateItem.value = insurance
                        ? [insurance?.payerName + ' - Eligiblity was run, but your payer is unavailable.']
                        : [];
                    break;
                case BenefitStatus.ProviderEnrollmentIssue:
                    insuranceEstimateItem.value = insurance
                        ? [insurance?.payerName + ' - There was an issue with the provider enrollment.']
                        : [];
                    break;
                case BenefitStatus.ResultsPending:
                    insuranceEstimateItem.value = insurance
                        ? [insurance?.payerName + ' - Eligibility is pending.']
                        : [];
                    break;
                case BenefitStatus.Error:
                    insuranceEstimateItem.value = insurance
                        ? [insurance?.payerName + ' - There was a problem verifying your insurance.']
                        : [];
                    break;
                case BenefitStatus.NonCovered:
                    insuranceEstimateItem.value = insurance
                        ? [insurance?.payerName + ' - This service is not covered by your insurance.']
                        : [];
                    break;
                case BenefitStatus.ActivePartA:
                    insuranceEstimateItem.value = insurance
                        ? [insurance?.payerName + ' - Your Medicare Part A insurance was verified.']
                        : [];
                    break;
                case BenefitStatus.ActivePartB:
                    insuranceEstimateItem.value = insurance
                        ? [insurance?.payerName + ' - Your Medicare Part B insurance was verified.']
                        : [];
                    break;
                case BenefitStatus.UnableToDetermine:
                    insuranceEstimateItem.value = insurance
                        ? [
                              insurance?.payerName +
                                  ' - Your insurance was verified, but we are unable to determine your coverage for this service. Please contact the number provided.',
                          ]
                        : [];
                    break;
                default:
                    insuranceEstimateItem.value = insurance?.payerName ? [insurance?.payerName] : [];
                    break;
            }
        } else {
            insuranceEstimateItem.value = insurance ? ['Select Insurance'] : [];
        }
    }

    private downloadPdf() {
        window.print();

        /*
const pdfData = await lastValueFrom(this.estimateService.estimationApiControllerGeneratePdf(this.sessionId, { template: htmlToPrint }));

const b64toBlob = (base64, type = 'application/octet-stream') => fetch(`data:${type};base64,${base64}`).then(res => res.blob());

// Convert the base64 PDF into a blob so that we can launch the browser to download the PDF.
const pdfBlob = new Blob([await b64toBlob(pdfData)], { type: 'application/pdf' });

// IE doesn't allow using a blob object directly as link href
// instead it is necessary to use msSaveOrOpenBlob
if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(pdfBlob);

    return;
}

// For other browsers:
// Create a link pointing to the ObjectURL containing the blob.
const data = window.URL.createObjectURL(pdfBlob);

const link = document.createElement('a');
link.href = data;
link.download = 'estimate.pdf';

// this is necessary as link.click() does not work on the latest firefox
link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));

setTimeout(() => {
    // For Firefox it is necessary to delay revoking the ObjectURL
    window.URL.revokeObjectURL(data);
    link.remove();
}, 100);*/
    }

    private async setSelectedProcedures(procedures: ProcedureGroupModel[]) {
        if (procedures) {
            this.selectedProcedures = procedures;
            const servicesEstimateItem = this.estimateItems.find((x) => x.id === 0);

            servicesEstimateItem.value = this.selectedProcedures.map((x) => {
                const procedure = x as any;
                const procedureName = procedure.name ?? procedure.procedureDisplayName;

                return this.translate.instant(procedureName);
            });

            servicesEstimateItem.selectedCount = this.selectedProcedures.length;

            await lastValueFrom(
                this.sessionService.shoppingSessionControllerUpdateProcedures(this.sessionId, {
                    procedures: this.selectedProcedures.map((x) => x.id.toString()),
                }),
            );
        }
    }

    private async setSessionSelectedProcedures(session: ShoppingSessionResponseModel) {
        if (session?.procedures?.length) {
            const selectedProcedureGroups = _.uniqBy(
                session.procedures.map((x) => x.procedureGroup as any),
                'id',
            );

            const mappedSelectedProcedureGroups = selectedProcedureGroups.map((x) =>
                this.procedureGroupsTranslation.config.find((translatedPg) => translatedPg.id === x.id),
            );
            Object.keys(this.procedureGroupsTranslation.language).forEach((lang) =>
                this.translate.setTranslation(lang, this.procedureGroupsTranslation.language[lang], true),
            );

            this.selectedProcedures = mappedSelectedProcedureGroups;
        } else {
            this.selectedProcedures = [];
        }

        const sevicesEstimateItem = this.estimateItems.find((x) => x.id === 0);

        sevicesEstimateItem.selectedCount = this.selectedProcedures.length;
        sevicesEstimateItem.value = this.selectedProcedures.map((x) => {
            const procedure = x as any;
            const procedureName = procedure.name ?? procedure.procedureDisplayName;
            return this.translate.instant(procedureName);
        });
    }

    private async setSelectedLocation(location: OrganizationLocation) {
        const locationItem = this.estimateItems.find((x) => x.id === 1);
        const previousLocationId = this.locationId;

        if (location) {
            locationItem.value = [location.name];
            locationItem.selectedCount = 1;
            this.locationId = location.id;
        } else {
            locationItem.value = null;
            locationItem.selectedCount = 0;
            this.locationId = null;
        }

        await lastValueFrom(
            this.sessionService.shoppingSessionControllerUpdateLocation(this.sessionId, {
                locationId: this.locationId,
            }),
        );

        const locationChanged = previousLocationId !== this.locationId;

        if (locationChanged) {
            const tasks: Promise<unknown>[] = [this.clearSelectedProcedures(), this.loadNotPerformedProcedures()];

            if (this.features?.truePriceShowLocations) {
                tasks.push(this.loadProcedures());
            }

            await Promise.all(tasks);
        }
    }

    private generateTextSummary(): string {
        let estimateSummary: string | undefined = undefined;

        if (this.estimate?.createdAt) {
            estimateSummary = `*_Estimate: ${this.translate.instant(this.estimate.estimateNumber)}\n`;
            this.estimate.serviceGroups.forEach(
                (group) => (estimateSummary += `Procedure: ${this.translate.instant(group.title?.toString())}\n`),
            );
            estimateSummary += `Cost: $${(this.estimate.summary as any).amount.toLocaleString()}\n`;
            estimateSummary += `Insurance: ${this.estimateItems.find((x) => x.id === 2).value ?? 'Not Selected'}\n`;
            estimateSummary += `Generated On: ${this.estimate.createdAt}`;
        } else {
            estimateSummary = 'No estimate generated. Please select services and try again.';
        }

        return estimateSummary;
    }

    private async setLocation(session: ShoppingSessionResponseModel): Promise<void> {
        // location feature is enabled ? set the location display name : hide location box

        if (!this.features?.truePriceShowLocations) {
            return;
        }

        this.locations = (
            await lastValueFrom(this.locationService.locationControllerGetSession(session.sessionId))
        ).locations;

        if (this.estimateItems[0].label !== 'Location') {
            this.estimateItems.unshift({
                id: 1,
                label: 'Location',
                placeholder: 'Select Location',
                value: null,
            });
        }

        if (session.locationName) {
            this.locationId = this.locations.find((x) => x.name === session.locationName)?.id;
            this.setLocationDisplayName(session.locationName);
        } else {
            const defaultLocation = this.locations.find((x) => x.defaultLocation);

            if (!session.procedures?.length && defaultLocation) {
                await this.setSelectedLocation(defaultLocation);

                await lastValueFrom(
                    this.sessionService.shoppingSessionControllerUpdateLocation(this.sessionId, {
                        locationId: defaultLocation.id,
                    }),
                );
            }
        }
    }

    private async clearSelectedProcedures() {
        this.selectedProcedures = [];

        const servicesEstimateItem = this.estimateItems.find((x) => x.id === 0);

        if (servicesEstimateItem) {
            servicesEstimateItem.value = null;
            servicesEstimateItem.selectedCount = 0;
        }

        await lastValueFrom(
            this.sessionService.shoppingSessionControllerUpdateProcedures(this.sessionId, { procedures: [] }),
        );
    }

    private async syncLatestSessionUpdates(existingSession: ShoppingSessionResponseModel = null) {
        let session: ShoppingSessionResponseModel | null = existingSession;
        if (!session) {
            session = await lastValueFrom(this.sessionService.shoppingSessionControllerGetSession(this.sessionId));
        }

        if (!session) {
            return;
        }

        this.setInsuranceDisplayName(session);
        this.setEstimateAcknowledged(session);
        await this.setSessionSelectedProcedures(session);
        await this.setLocation(session);
        await this.setPrintHeader(session);
    }
}
